import type {SourceOrderProvenance} from "./sourceOrderProvenance";
export type Role = "admin" | "manager" | "operator" | "pharmacy";

export type Section =
  | "dashboard"
  | "audit"
  | "clients"
  | "purchases"
  | "sales"
  | "catalog"
  | "inventory"
  | "finance"
  | "retail"
  | "pharmacy_money"
  | "operator_cash"
  | "tasks"
  | "settings";

export interface NavItem {
  id: Section;
  label: string;
  icon: string;
  roles: Role[];
}

export interface DashboardSnapshot {
  generatedAt: string;
  period: {
    dateFrom: string;
    dateTo: string;
    timezone: "Europe/Moscow";
  };
  catalog: {
    products: number;
    withStock: number;
    negativeStock: number;
    reserved: number;
  };
  operations: {
    customerOrders: number;
    retailSales: number;
    supplies: number;
    transfers: number;
  };
  structure: {
    legalEntities: number;
    warehouses: number;
    retailPoints: number;
    suppliers: number;
  };
  orders: {
    created: number;
    confirmed: number;
    fulfilled: number;
    stages: Record<string, number>;
    lifecycle: Record<string, number>;
  };
  sales: {
    documents: number;
    returns: number;
    revenueMinor?: number;
  };
  receipts: {
    documents: number;
    amountMinor?: number;
  };
  payments?: {
    documents: number;
    incomingMinor: number;
    outgoingMinor: number;
  };
  companyCash?: {
    balanceMinor: number;
    pharmacyHandoversMinor: number;
    deliveryCashMinor: number;
    manualIncomingMinor: number;
    manualOutgoingMinor: number;
    activeOperations: number;
  };
  pharmacyCash?: {
    balanceMinor: number;
    incompletePoints: number;
    points: Array<{
      pointId: string;
      pointName: string;
      warehouseId: string;
      warehouseName: string;
      balanceMinor: number;
      openingCashMinor: number;
      localCashSalesMinor: number;
      cashReturnsMinor: number;
      pharmacyExpensesMinor: number;
      handoverIssuedMinor: number;
      sourceComplete: boolean;
    }>;
  };
  economics?: {
    revenueMinor: number;
    costMinor: number;
    grossProfitMinor: number;
  };
  attention: AttentionItem[];
}

export interface DashboardSalesPrimaryData {
  kind: "shipment" | "retail_sale" | "customer_return" | "fiscal_sale" | "fiscal_return";
  id: string;
  number: string;
  occurredAt: string;
  amountMinor: number;
  registrySection: "sales" | "retail";
  registryQuery: string;
}

export interface DashboardSalesDrilldownPageData {
  items: DashboardSalesPrimaryData[];
  total: number;
  salesDocuments: number;
  returns: number;
  revenueMinor: number;
  offset: number;
  limit: number;
  sort: "occurred_desc" | "occurred_asc";
}

export interface AttentionItem {
  kind: "danger" | "warning" | "info";
  title: string;
  detail: string;
  action: string;
  section: Section;
}

export interface CatalogItem {
  itemType?: "goods" | "service";
  id: string;
  name: string;
  code: string;
  article?: string;
  barcode?: string;
  uomCode?: string;
  version: number;
  salePriceMinor?: number;
  priceVersion: number;
  vatMode: "unconfigured" | "without_vat" | "rate";
  vatRate?: 0 | 5 | 7 | 10 | 20 | 22 | null;
  buyPriceMinor?: number | null;
  stockValueMinor?: number | null;
  costCoverage?: "complete" | "incomplete" | "none";
  unvaluedStock?: number | string;
  stock?: number;
  reserve?: number;
  available?: number;
  blocked?: number;
  inTransit?: number;
  nearestExpiryOn?: string;
  lastReceivedAt?: string;
  lotCount?: number;
  stockScope: "network" | "warehouse" | "unavailable";
  trackingType: string;
  lifecycleStatus: "draft" | "active";
  missingUom: boolean;
  updated: string;
}

export interface CatalogPageData {
  items: CatalogItem[];
  total: number;
  offset: number;
  limit: number;
  source: "moysklad_audit" | "pharmhub" | "postgres";
}

export interface CatalogExpiryBreakdownData {
  productId: string;
  stockScope: "network" | "warehouse";
  items: Array<{
    lotId: string;
    lotNumber?: string | null;
    receivedAt: string;
    warehouseId: string;
    warehouseName: string;
    expiresOn: string | null;
    stock: string;
    reserve: string;
    available: string;
  }>;
}

export interface ProductHistoryEntryData {
  id: string;
  kind: "audit" | "movement";
  occurredAt: string;
  action: string;
  actorDisplayName?: string;
  actorRole?: string;
  warehouseName?: string;
  lotNumber?: string;
  documentType?: string;
  documentId?: string;
  quantity?: string;
  unitCostMinor?: number | null;
  previousPriceMinor?: number;
  newPriceMinor?: number;
  priceVersion?: number;
  priceReason?: string;
  sandbox: boolean;
}

export interface ProductHistoryPageData {
  items: ProductHistoryEntryData[];
  total: number;
  offset: number;
  limit: number;
}

export interface ManagerOrderProductSearchItemData {
  productId: string;
  productName: string;
  salePriceMinor: number;
  availableQuantity: string;
  defaultLotId?: string;
  lots: OrderLotOptionData[];
}

export interface ManagerOrderProductSearchPageData {
  items: ManagerOrderProductSearchItemData[];
  hasMore: boolean;
}

export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
  role: Role;
}

export interface CurrentSession {
  user: SessionUser;
  mode: "session" | "demo";
}

export interface AuditEventData {
  id: string;
  occurredAt: string;
  action: string;
  actorUserId?: string;
  actorDisplayName?: string;
  actorRole?: string;
  subjectType?: string;
  subjectId?: string;
  correlationId: string;
  sandbox: boolean;
}

export interface AuditEventPageData {
  items: AuditEventData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LoginResult {
  user: SessionUser;
  csrfToken: string;
  expiresAt: string;
}

export interface InventoryOverviewData {
  scope: "network" | "warehouse" | "unavailable";
  warehouses: Array<{ id: string; name: string; kind: "central" | "pharmacy" | "unknown" }>;
  metrics: {
    products: number;
    withStock?: number;
    positiveStock?: number;
    negativeStock?: number;
    reserved?: number;
    transfers: number;
    receipts: number;
    writeOffs: number;
    inventories: number;
  };
  flows: Array<{
    sourceWarehouseId: string;
    sourceName: string;
    targetWarehouseId: string;
    targetName: string;
    documents: number;
  }>;
  source: "moysklad_audit" | "pharmhub" | "postgres";
}

export interface TransferItemData {
  productId: string;
  productName: string;
  requestedQuantity: string;
  shippedQuantity: string;
  receivedQuantity: string;
  lotId?: string;
  expiresOn?: string | null;
}

export interface TransferData {
  id: string;
  number: string;
  sourceType: "manual" | "execution_request" | "customer_order_return" | "pharmacy_return";
  sourceDocumentId?: string | null;
  sourceDocumentNumber?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  readOnly: boolean;
  sourceWarehouseId: string;
  sourceWarehouseName?: string | null;
  sourceWarehouseKind?: "central" | "pharmacy" | null;
  targetWarehouseId: string;
  targetWarehouseName?: string | null;
  targetWarehouseKind?: "central" | "pharmacy" | null;
  assigneeUserId?: string;
  status: "draft" | "picking" | "in_transit" | "discrepancy" | "received" | "cancelled";
  posted: boolean;
  postedAt?: string | null;
  canCancel: boolean;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  items: TransferItemData[];
  sandbox: boolean;
}

export interface PharmacyReturnContextData {
  routes: Array<{
    source: InventoryOverviewData["warehouses"][number];
    targets: InventoryOverviewData["warehouses"];
  }>;
  operators: AssignableUserData[];
  assignments: Array<{
    sourceWarehouseId: string;
    targetWarehouseId: string;
    operatorIds: string[];
  }>;
}

export interface TransferPageData { items: TransferData[]; total: number }

export interface SandboxLotData {
  id: string;
  legalEntityId?: string;
  legalEntityName?: string;
  warehouseId: string;
  warehouseName: string;
  supplierId?: string;
  supplierName?: string;
  productId: string;
  productName: string;
  lotNumber: string;
  expiresOn?: string | null;
  quantity: string;
  reservedQuantity: string;
  availableQuantity: string;
  unitCostMinor?: number | null;
  stockValueMinor?: number | null;
  valuationStatus?: "known" | "unknown";
  valuationVersion?: number;
  receivedAt: string;
  sourceReceiptId?: string;
  sourceStocktakeId?: string;
  disposition: "available" | "quarantine" | "blocked";
  admissible: boolean;
  physicalReturnEligibility?:{eligible:boolean;availableQuantity:string;blockingReason?:string|null};
  blockingReason?: string;
  version: number;
}

export interface SandboxLotPageData { items: SandboxLotData[]; total: number; movementCount: number; sandbox: boolean }

export interface WriteOffData {
  id: string;
  number: string;
  lotId: string;
  productId: string;
  productName: string;
  lotNumber: string;
  expiresOn?: string | null;
  warehouseId: string;
  warehouseName: string;
  status: "draft" | "posted" | "cancelled";
  posted: boolean;
  quantity: string;
  reason: string;
  unitCostMinor?: number;
  totalMinor?: number;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  postedAt?: string | null;
  sandbox: boolean;
}

export interface WriteOffPageData { items: WriteOffData[]; total: number }

export interface StocktakeLineData {
  lotId: string;
  productId: string;
  productName: string;
  lotNumber?: string | null;
  expiresOn: string | null;
  expectedQuantity: string;
  countedQuantity?: string;
  differenceQuantity?: string;
  unitCostMinor?: number | null;
  adjustmentValueMinor?: number | null;
  valuationComplete?: boolean | null;
}

export interface StocktakeData {
  id: string;
  number: string;
  warehouseId: string;
  warehouseName: string;
  assigneeUserId: string;
  status: "draft" | "counting" | "counted" | "posted" | "cancelled";
  posted: boolean;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  countedAt?: string;
  postedAt?: string;
  lines: StocktakeLineData[];
  totalChangeMinor?: number | null;
  valuationComplete?: boolean | null;
  sandbox: boolean;
}

export interface StocktakePageData { items: StocktakeData[]; total: number }

export interface ProcurementOverviewData {
  purchaseOrders: number;
  receipts: number;
  receiptPositions: number;
  supplierReturns: number;
  supplierInvoices: number;
  supplierCount: number;
  totalMinor?: number;
  paidMinor?: number;
  paymentCoverageBps?: number;
  earliestMoment?: string;
  latestMoment?: string;
  suppliers: Array<{ id: string; name: string; receipts: number }>;
  warehouses: Array<{ id: string; name: string; receipts: number }>;
  facts: string[];
  source: "moysklad_audit" | "postgres";
}

export interface ProcurementReferenceData {
  legalEntities: Array<{ id: string; name: string }>;
  suppliers: Array<{ id: string; name: string }>;
  warehouses: Array<{ id: string; name: string }>;
}

export interface CounterpartyData {
  id: string;
  name: string;
  kind: "supplier" | "customer" | "both";
  companyType: "legal" | "entrepreneur" | "individual";
  inn?: string;
  kpp?: string;
  phone?: string;
  email?: string;
  address?: string;
  note?: string;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  source: "moysklad_audit" | "sandbox";
  sandbox: boolean;
}

export interface CounterpartyPageData { items: CounterpartyData[]; total: number }

export interface PurchaseOrderData {
  stockIncludedAtCutover?: boolean;
  id: string;
  number: string;
  legalEntityId: string;
  legalEntityName: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseName: string;
  status: "draft" | "approved" | "sent" | "partial" | "received" | "cancelled";
  /** Posting is independent from the operational workflow status. */
  posted: boolean;
  postedAt?: string;
  documentDate: string;
  expectedOn?: string;
  totalMinor?: number;
  assigneeUserId?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  items: Array<{ productId: string; productCode?: string; productName: string; quantity: string; receivedQuantity: string; unitCostMinor?: number; lineTotalMinor?: number }>;
  receipts: Array<{ id: string; number: string; documentDate: string; occurredAt: string; totalMinor?: number; lines: Array<{ lotId?: string; productId: string; productCode?: string; productName: string; quantity: string; lotNumber: string; expiresOn?: string | null; unitCostMinor?: number }> }>;
  receiptHistory?: PurchaseOrderData["receipts"];
  correctionRevisionId?: string;
  sandbox: boolean;
}

export interface PurchaseOrderPageData { items: PurchaseOrderData[]; total: number }

export interface PhysicalReceivingBatchData {
  lotId: string;
  lotNumber: string;
  expiresOn?: string | null;
  quantity: string;
  recordedAt: string;
}

export interface PhysicalReceivingLineData {
  productId: string;
  productCode?: string;
  productName: string;
  expectedQuantity: string;
  receivedQuantity: string;
  batches: PhysicalReceivingBatchData[];
}

export interface PhysicalReceivingData {
  stockIncludedAtCutover?: boolean;
  id: string;
  number: string;
  sourceType: "purchase_order" | "incoming_invoice";
  sourceDocumentId: string;
  sourceDocumentNumber: string;
  warehouseId: string;
  warehouseName: string;
  assigneeUserId: string;
  assigneeDisplayName: string;
  status: "assigned" | "in_progress" | "partial" | "completed" | "disputed" | "cancelled";
  discrepancy: boolean;
  discrepancyComment?: string;
  resolutionComment?: string;
  expiryFilled: boolean;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  lines: PhysicalReceivingLineData[];
  sandbox: true;
}

export interface PhysicalReceivingPageData { items: PhysicalReceivingData[]; total: number }

export interface PhysicalReceivingCommandResultData {
  state: "partial" | "disputed" | "committed" | "recovery_required";
  receiving: PhysicalReceivingData;
  recoveryStage?: string;
}

export interface PurchaseReceiptJournalData {
  id: string;
  number: string;
  orderId: string;
  orderNumber: string;
  legalEntityId: string;
  legalEntityName: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseName: string;
  documentDate: string;
  occurredAt: string;
  totalMinor?: number;
  lines: Array<{ lotId?: string; productId: string; productCode?: string; productName: string; quantity: string; lotNumber: string; expiresOn?: string | null; unitCostMinor?: number }>;
  sandbox: boolean;
}

export interface PurchaseReceiptJournalPageData { items: PurchaseReceiptJournalData[]; total: number }

export interface SupplierReturnData {
  id: string;
  number: string;
  sourceReceiptId: string;
  sourceReceiptNumber: string;
  supplierId: string;
  supplierName: string;
  warehouseId: string;
  warehouseName: string;
  status: "draft" | "posted" | "cancelled";
  reason: string;
  totalMinor?: number;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  postedAt?: string;
  items: Array<{ lotId?: string; productId: string; productName: string; quantity: string; lotNumber: string; expiresOn: string | null; unitCostMinor?: number; lineTotalMinor?: number }>;
  sandbox: boolean;
}

export interface SupplierReturnPageData { items: SupplierReturnData[]; total: number }

export interface SalesOverviewData {
  customerOrders: number;
  orderPositions: number;
  customerInvoices: number;
  shipments: number;
  retailSales: number;
  retailPositions: number;
  orderStates: Array<{ name: string; count: number }>;
  shipmentStates: Array<{ name: string; count: number }>;
  payments: { cashMinor: number; cashlessMinor: number; qrMinor: number };
  earliestMoment?: string;
  latestMoment?: string;
  source: "moysklad_audit";
}

export interface CustomerOrderData {
  id:string; number:string; orderNumber?:number; customerLabel:string; warehouseId:string; warehouseName:string;
  reservationWarehouseId?:string; reservationWarehouseName?:string;
  importedFrom?:"moysklad"|null;
  sourceProvenance?:SourceOrderProvenance|null;
  sourceProvenanceAvailability?:{state:"unavailable"}|null;
  clientId?:string; customerCrmLevel?:"new"|"buyer"|"repeat"|"regular"|"premium"|"vip"; customerPhone?:string; source:"phone"|"telegram"|"whatsapp"|"website"|"manual"; requestedFor?:string; deliveryAddress?:string;
  workflowStatus:"new"|"confirmed"|"reserved"|"assigned"|"picking"|"ready"|"received"|"fulfilled"|"closed"|"cancelled";
  cancellationReason?:string;
  primaryStatus:"Самовывоз"|"Постоянный"|"Закрыт"|"Доставка"|"Почта"|"Отменен"|"Оплачено"|"В Работе"|"Предоплата";
  posted:boolean; reservationStatus:"none"|"partial"|"full"|"released";
  reservationProcessStatus:"not_started"|"pending"|"succeeded"|"failed"|"released";
  reservationGeneration:number; reservationErrorCode?:string;
  fulfilmentMode:"pharmacy"|"pickup"|"delivery"|"shipping"; paymentStatus:"unpaid"|"prepaid"|"paid";
  prescriptionRequired:boolean; totalMinor?:number; assigneeUserId?:string; createdByUserId?:string; version:number; documentDate:string; createdAt:string; updatedAt:string; sandbox:boolean;
  items:Array<{productId:string;productName:string;quantity:string;reservedQuantity:string;fulfilledQuantity:string;uncoveredQuantity:string;receivedQuantity?:string;backorderQuantity:string;plannedExpiryMonth?:string;supplyStatus:"reserved"|"partial_backorder"|"awaiting_procurement"|"reservation_attention_required";unitPriceMinor?:number;lineTotalMinor?:number;lots:Array<{lotId:string;lotNumber:string;expiresOn:string|null;quantity:string}>;trackingType:string;scannedPackages:number}>;
}
export interface CustomerOrderPageData { items:CustomerOrderData[]; total:number }
export interface CustomerOrderRestoreEligibilityData {
  orderId:string;orderVersion:number;eligible:boolean;
  reasonCode:"eligible"|"not_cancelled"|"supply_not_released"|"execution_or_stock_history"|"not_current_cancellation"|"dependent_document_history";
  message:string;
}

export interface CrmOrderLinkDecisionData {
  orderId:string;
  crmLinkStatus:"active"|"ignored";
  canonicalLinkedCalls:number;
  removedCanonicalLinks:number;
  linkStateToken:string;
}

export interface OrderLotOptionData { id:string;warehouseId:string;productId:string;productName:string;lotNumber:string;expiresOn:string|null;availableQuantity:string;admissible:boolean;fefoRank:number;blockingReason?:string }
export interface OrderLotOptionPageData { items:OrderLotOptionData[];total:number }

export interface ClientData {
  id:string; displayName:string; phone:string; email?:string; telegram?:string; whatsapp?:string;
  defaultAddress?:string; defaultSource:"phone"|"telegram"|"whatsapp"|"website"|"manual"; note?:string;
  isActive:boolean; version:number; ordersCount:number; lastOrderAt?:string; createdAt:string; updatedAt:string;
  phones:Array<{value:string;isPrimary:boolean}>; sourceSystem:string;
  crmLevel:"new"|"buyer"|"repeat"|"regular"|"premium"|"vip";
  manualControlStatus:"none"|"risk"; clientStatus:string;
  riskReason?:string; riskComment?:string; riskChangedByUserId?:string; riskChangedAt?:string;
  callsCount:number; incomingCalls:number; outgoingCalls:number; lastCallAt?:string;
  successfulOrders:number; failedOrders:number;
}
export interface ClientPageData { items:ClientData[]; total:number }
export interface ClientOrderSummaryData { id:string;number:string;sourceStatus:string;crmResult:string;amountMinor:number;moment?:string;sourceSystem:"xc_crm"|"pharmhub" }
export interface ClientOrderPageData { items:ClientOrderSummaryData[];total:number }
export interface ClientMergeTransferCountsData { phones:number;identities:number;calls:number;operationalOrders:number;legacyOrders:number;orderLinks:number }
export interface ClientMergeOrderScopeData { legalEntityId:string;legalEntityLabel:string;warehouseId:string;warehouseLabel:string;ordersCount:number }
export interface ClientMergePreviewData {
  winner:ClientData;loser:ClientData;willMove:ClientMergeTransferCountsData;after:ClientMergeTransferCountsData;
  orderScopes:ClientMergeOrderScopeData[];canMerge:boolean;blockers:Array<{code:string;message:string}>;stateToken?:string;
  identityScope:"global_client";callLegalEntityScopeVerified:boolean;
}
export interface ClientMergeResultData { canonicalClient:ClientData;mergedClientId:string;mergeEventId:string;moved:ClientMergeTransferCountsData;occurredAt:string }
export interface LegacyMoneyReportData { id:string;kind:string;label:string;status:string;amountMinor:number;occurredAt?:string;responsible?:string }
export interface LegacyMoneyReportPageData { items:LegacyMoneyReportData[];total:number }
export interface LegacyStockRowData { id:string;productId:string;productName:string;warehouseName:string;quantity:string;minimumQuantity:string;updatedAt?:string }
export interface LegacyStockRowPageData { items:LegacyStockRowData[];total:number }

export interface LegacyOpsItemData {
  productId:string; productName:string; quantity:string; priceMinor?:number;
  status?:string; receivedQuantity?:string;
}
export interface LegacyOpsOrderData {
  id:string; moyskladId:string; number:string; customerName:string; sourceStatus:string;
  workflowStatus:string; occurredAt:string; updatedAt:string; items:LegacyOpsItemData[];
  sourceSystem:"xc_ops"; readOnly:true;
}
export interface LegacyOpsOrderPageData { items:LegacyOpsOrderData[];total:number }

export interface MoySkladInformationOrderItemData {
  productId?: string;
  productName: string;
  quantity: number;
}

export interface MoySkladInformationOrderData {
  id: string;
  number: string;
  customerName: string;
  stateName: string;
  moment: string;
  plannedAt?: string;
  sourceUpdatedAt: string;
  businessDate: string;
  version: number;
  sourceSystem: "moysklad";
  readOnly: true;
  items: MoySkladInformationOrderItemData[];
}

export interface MoySkladInformationOrderPageData {
  items: MoySkladInformationOrderData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyOpsTaskData {
  id:string; number:string; taskType:string; status:string; title:string; description?:string;
  orderId?:string; assignedToUserId?:string; createdByUserId?:string; pharmacyId?:string;
  destination?:string; deadlineAt?:string; createdAt:string; updatedAt:string;
  items:LegacyOpsItemData[]; sourceSystem:"xc_ops"; readOnly:true;
}
export interface LegacyOpsTaskPageData { items:LegacyOpsTaskData[];total:number }

export interface CustomerInvoiceData {
  id:string; number:string; orderId:string; orderNumber:string; customerLabel:string;
  status:"draft"|"issued"|"cancelled"; totalMinor:number; version:number;
  posted:boolean; postedAt?:string|null;
  documentDate:string; createdAt:string; updatedAt:string; issuedAt?:string; sandbox:boolean;
}
export interface CustomerInvoicePageData { items:CustomerInvoiceData[]; total:number }

export interface CustomerShipmentLineData {
  lotId:string; productId:string; productName:string; lotNumber:string; expiresOn?:string|null; quantity:string;
  unitPriceMinor?:number; lineTotalMinor?:number; unitCostMinor?:number; costTotalMinor?:number; grossProfitMinor?:number;
}
export interface CustomerShipmentData {
  id:string; number:string; orderId:string; orderNumber:string; customerLabel:string; warehouseId:string;
  assigneeUserId?:string; status:"draft"|"posted"|"cancelled"; totalMinor?:number;
  posted:boolean;
  costTotalMinor?:number; grossProfitMinor?:number; version:number; documentDate:string; createdAt:string; updatedAt:string;
  postedAt?:string; items:CustomerShipmentLineData[]; sandbox:boolean;
}
export interface CustomerShipmentPageData { items:CustomerShipmentData[]; total:number }

export interface CustomerReturnData {
  id:string; number:string; shipmentId:string; shipmentNumber:string; orderId:string; customerLabel:string;
  status:"draft"|"posted"|"cancelled"; reason:string; totalMinor:number; costTotalMinor?:number;
  posted:boolean;
  grossProfitImpactMinor?:number; version:number; documentDate:string; createdAt:string; updatedAt:string; postedAt?:string;
  items:CustomerShipmentLineData[]; sandbox:boolean;
}
export interface CustomerReturnPageData { items:CustomerReturnData[]; total:number }

export interface RetailOverviewData {
  scope: "network" | "warehouse" | "unavailable";
  points: Array<{ id: string; name: string; warehouseId?: string; deviceStatus: "not_configured" | "online" | "offline" | "blocked" }>;
  shiftsTotal: number;
  shiftsOpen: number;
  shiftsClosed: number;
  retailSales: number;
  retailReturns: number;
  cashIns: number;
  cashOuts: number;
  canOpenPos: boolean;
  sandboxOperationsAllowed: boolean;
  sandboxPrepaymentsAvailable: boolean;
  source: "moysklad_audit" | "postgres";
}

export interface RetailClientSafetySnapshotData {
  orderId:string; orderNumber:string; clientId?:string; clientName:string;
  crmLevel?:"new"|"buyer"|"repeat"|"regular"|"premium"|"vip";
  manualControlStatus:"none"|"risk"; riskReason?:string; riskComment?:string;
  clientVersion?:number; requiresNewAcknowledgement:boolean; requiresRiskAcknowledgement:boolean;
}

export interface RetailClientSafetyAcknowledgementData {
  id:string; orderId:string; orderNumber:string; clientId:string; actorUserId:string;
  actorRole:"admin"|"pharmacy"; acknowledgedNew:boolean; acknowledgedRisk:boolean;
  crmLevel:"new"|"buyer"|"repeat"|"regular"|"premium"|"vip";
  manualControlStatus:"none"|"risk"; riskReason?:string; riskComment?:string;
  clientVersion:number; acknowledgedAt:string;
}

export interface RetailQuoteData {
  lines: Array<{
    productId: string;
    name: string;
    barcode?: string;
    quantity: string;
    unitPriceMinor: number;
    catalogPriceMinor: number;
    orderPriceMinor?: number;
    lotAllocations: Array<{ lotId: string; expiresOn: string | null; quantity: string }>;
    priceOverrideReason?: string;
    priceOverrideAdmin?: string;
    lineTotalMinor: number;
    unitCostMinor?: number;
    costTotalMinor?: number;
    grossProfitMinor?: number;
    available?: string;
    stockScope: "network" | "warehouse" | "unavailable";
  }>;
  totalMinor: number;
  paymentMethod: "cash" | "card" | "qr" | "mixed";
  paymentAllocations: Array<{ method: "cash" | "card" | "qr" | "prepayment"; amountMinor: number }>;
  executable: boolean;
  blockers: string[];
  source: "moysklad_audit" | "postgres";
}

export interface RetailShiftData {
  id: string;
  pointId?: string;
  status: "opening" | "open" | "closing" | "closed" | "recovery";
  openingCashMinor: number;
  expectedCashMinor?: number | null;
  actualCashMinor?: number | null;
  closeComment?: string;
  openedAt: string;
  closedAt?: string;
  version: number;
  sandbox: boolean;
}

export type ShiftReportStatus =
  | "draft"
  | "submitted"
  | "accepted"
  | "returned_to_fix"
  | "problem";

/** Server-calculated operational totals and explicit primary-source coverage. */
export interface ShiftReportTotalsData {
  openingCashMinor?: number | null;
  expectedCashMinor?: number | null;
  actualCashMinor?: number | null;
  discrepancyMinor?: number | null;
  completedSaleCount?: number | null;
  cashSalesMinor?: number | null;
  cardSalesMinor?: number | null;
  qrSalesMinor?: number | null;
  prepaymentsReceivedMinor?: number | null;
  prepaymentsAppliedMinor?: number | null;
  cashReturnsMinor?: number | null;
  cashlessReturnsMinor?: number | null;
  cashInMinor?: number | null;
  cashOutMinor?: number | null;
  handoverMinor?: number | null;
  handoverAcceptedMinor?: number | null;
  handoverPendingMinor?: number | null;
  handoverProblemMinor?: number | null;
  deliveryCount?: number | null;
  deliveryTotalMinor?: number | null;
  shippingCount?: number | null;
  shippingTotalMinor?: number | null;
  calculationTotalMinor?: number | null;
  operationalTurnoverMinor?: number | null;
}

export interface ShiftReportSourceCoverageData {
  sandboxSales: boolean;
  paymentAllocations: boolean;
  returns: boolean;
  cashMovements: boolean;
  cashHandovers: boolean;
  deliveries: boolean;
  shipping: boolean;
  corrections: boolean;
}

export interface ShiftReportData {
  id: string;
  number: string;
  shiftId: string;
  pointId: string;
  warehouseId?: string;
  businessDate: string;
  status: ShiftReportStatus;
  sourceKind: "operational_sandbox";
  revision: number;
  correctsReportId?: string;
  version: number;
  totals: ShiftReportTotalsData;
  sourceCoverage: ShiftReportSourceCoverageData;
  sourceComplete: boolean;
  pharmacyAction?: "submit" | "create_correction";
  submissionAvailable: boolean;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  decidedAt?: string;
  decisionReason?: string;
}

export interface PaymentAllocationEvidenceData {
  saleId: string;
  method: "cash" | "card" | "qr";
  amountMinor: number;
  saleTotalMinor: number;
}

export interface PaymentAllocationEvidencePageData {
  reportId: string;
  shiftId: string;
  sourceKind: "operational_sandbox";
  allocations: PaymentAllocationEvidenceData[];
}

export interface ShiftReportPageData {
  items: ShiftReportData[];
  total: number;
  offset: number;
  limit: number;
}

export type DailyCashReportStatus = "submitted" | "accepted" | "returned_to_fix" | "problem";

export interface DailyCashReportTotalsData {
  openingCashMinor:number;
  localCashSalesMinor:number;
  cardSalesMinor:number;
  qrSalesMinor:number;
  remoteCashExcludedMinor:number;
  remoteCashlessMinor:number;
  cashReturnsMinor:number;
  pharmacyExpensesMinor:number;
  handoverIssuedMinor:number;
  handoverCompletedMinor:number;
  handoverPendingMinor:number;
  handoverProblemMinor:number;
  closingCashMinor:number;
}

export interface DailyCashReportPreviousSourceData {
  id:string;
  number?:string;
  status?:DailyCashReportStatus;
  closingCashMinor:number;
}

export type DailyCashReportSourceKind =
  | "daily_cash_report" | "retail_shift" | "retail_sale" | "pharmacy_cash_sources" | "pharmacy_cash_opening"
  | "fiscal_return" | "pharmacy_cash_expense" | "cash_handover";

export interface DailyCashReportSourceReferenceData {
  sourceKind:DailyCashReportSourceKind;
  sourceId:string;
  sourceNumber:string;
  documentDate:string;
  drillDownPath:string;
  drillDownRoute:string;
}

export interface DailyCashReportOpeningSourceData extends DailyCashReportSourceReferenceData {
  sourceKind:"daily_cash_report"|"retail_shift"|"pharmacy_cash_sources"|"pharmacy_cash_opening";
  amountMinor:number;
  status?:DailyCashReportStatus;
}

export interface DailyCashReportSaleSourceData extends DailyCashReportSourceReferenceData {
  sourceKind:"retail_sale";
  mode:"pharmacy"|"pickup"|"delivery"|"shipping";
  totalMinor:number;
  cashMinor:number;
  cardMinor:number;
  qrMinor:number;
  finalizedAt:string;
}

export interface DailyCashReportReturnSourceData extends DailyCashReportSourceReferenceData {
  sourceKind:"fiscal_return";
  mode:"pharmacy"|"pickup"|"delivery"|"shipping";
  amountMinor:number;
  excludedFromPharmacyCash:boolean;
  finalizedAt:string;
}

export interface DailyCashReportExpenseSourceData extends DailyCashReportSourceReferenceData {
  sourceKind:"pharmacy_cash_expense";
  amountMinor:number;
}

export interface DailyCashReportHandoverSourceData extends DailyCashReportSourceReferenceData {
  sourceKind:"cash_handover";
  status:"issued_by_pharmacy"|"disputed"|"completed";
  amountMinor:number;
  issuedAt:string;
}

export interface DailyCashReportSourceEvidenceData {
  snapshotSchemaVersion:number;
  sourceHashSchemaVersion:number;
  sourceHash:string;
  periodStart:string;
  periodEnd:string;
  openingSource?:DailyCashReportOpeningSourceData;
  previousReport?:DailyCashReportPreviousSourceData;
  sales:DailyCashReportSaleSourceData[];
  returns:DailyCashReportReturnSourceData[];
  expenses:DailyCashReportExpenseSourceData[];
  handovers:DailyCashReportHandoverSourceData[];
}

export interface DailyCashReportSourceDetailData extends DailyCashReportSourceReferenceData {
  reportId:string;
  reportNumber:string;
  snapshot:Record<string,string|number|boolean|null>;
}

export interface DailyCashReportData {
  id:string;
  number:string;
  pointId:string;
  pointName:string;
  warehouseId:string;
  businessDate:string;
  periodStart:string;
  periodEnd:string;
  revision:number;
  correctsReportId?:string;
  previousReportId?:string;
  status:DailyCashReportStatus;
  submissionKind:"auto_21"|"correction";
  sourceComplete:boolean;
  totals:DailyCashReportTotalsData;
  version:number;
  submittedAt:string;
  decidedAt?:string;
  decisionReason?:string;
  createdAt:string;
  acceptanceBlockers:string[];
  sourceEvidence?:DailyCashReportSourceEvidenceData|null;
}

export interface DailyCashReportPageData {
  items:DailyCashReportData[];
  total:number;
  offset:number;
  limit:number;
}

export type PharmacyCashExpenseCategory = "operating"|"courier"|"supplies"|"service"|"other";

export interface PharmacyCashExpenseData {
  id:string;
  number:string;
  pointId:string;
  pointName:string;
  warehouseId:string;
  category:PharmacyCashExpenseCategory;
  counterparty:string;
  amountMinor:number;
  comment?:string;
  status:"posted"|"cancelled";
  version:number;
  documentDate:string;
  createdAt:string;
  updatedAt:string;
  cancellationReason?:string;
}

export interface PharmacyCashExpensePageData {
  items:PharmacyCashExpenseData[];
  total:number;
  offset:number;
  limit:number;
}

export interface RetailSaleDraftData {
  id: string;
  shiftId: string;
  pointId?: string;
  customerOrderId?: string;
  orderNumber?: number;
  customerLabel?: string;
  status: "draft" | "completed_sandbox" | "cancelled";
  paymentMethod: "cash" | "card" | "qr" | "mixed";
  paymentAllocations: Array<{ method: "cash" | "card" | "qr" | "prepayment"; amountMinor: number }>;
  lines: RetailQuoteData["lines"];
  totalMinor: number;
  orderTotalMinor?: number;
  priceDifferenceMinor?: number;
  vatRate?: 0 | 5 | 7 | 10 | 20 | 22;
  costTotalMinor?: number;
  profitMinor?: number;
  relatedDocuments: Array<{
    id: string;
    kind: "return" | "correction";
    number: string;
    status: string;
    documentDate: string;
  }>;
  blockers: string[];
  fiscalEligible: boolean;
  version: number;
  documentDate: string;
  createdAt: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: string;
  sandbox: boolean;
}

export interface FiscalCapabilityData {
  enabled: boolean;
  safeCode: string;
  receiptEnabled?: boolean;
  receiptSafeCode?: string;
  observedShiftState?: "open" | "closed" | "expired" | null;
  observedShiftNumber?: number | null;
  observedAt?: string;
  deviceId?: string | null;
  missingContracts?: string[];
  xReportEnabled?: boolean;
  xReportSafeCode?: string;
  allowedPaymentMethods?: Array<"cash" | "card" | "mixed">;
  noncashConfirmationMode?: "manual_external_received";
  remoteNoncashEnabled?: boolean;
}

export interface SignedFiscalJobBodyData {
  jobId: string;
  protocolVersion: "2.0" | string;
  command: "receipt" | string;
  issuedAt: string;
  expiresAt: string;
}

export interface SignedFiscalJobData {
  body: SignedFiscalJobBodyData;
  signature: string;
}

export interface RealFiscalSaleInitiationData {
  realSaleId: string;
  sourceDraftId: string;
  orderId: string;
  shiftId: string;
  job: SignedFiscalJobData;
  replayed: boolean;
}

export type RealFiscalSaleResultState = "pending" | "ready" | "failed" | "recovery_required";

export interface RealFiscalSaleResultData {
  realSaleId: string;
  sourceDraftId: string;
  orderId: string;
  executionRequestId?: string | null;
  retailPointId: string;
  jobId: string;
  state: RealFiscalSaleResultState;
  saleStatus: string;
  jobStatus: string;
  safeCode?: string | null;
  fiscalDocumentNumber?: number | null;
  documentDate: string;
  fiscalOccurredAt?: string | null;
  finalized: boolean;
  retryAllowed: boolean;
  noEffectResolved: boolean;
  resolutionRequired: boolean;
  freshDraftRequired: boolean;
  deliveryRequestStatus?: string | null;
}

export interface RealFiscalSaleRegistryItemData {
  realSaleId: string;
  orderNumber: string;
  retailPointId: string;
  retailPointName: string;
  shiftId: string;
  fiscalShiftNumber?: number | null;
  state: RealFiscalSaleResultState;
  saleStatus: string;
  totalMinor: number;
  paymentMethods: Array<"cash" | "card" | "qr">;
  fulfilmentMode?: "pharmacy" | "pickup" | "delivery" | "shipping" | null;
  documentDate: string;
  createdAt: string;
  fiscalDocumentNumber?: number | null;
  fiscalOccurredAt?: string | null;
  finalized: boolean;
  sourceDraftId?: string;
  orderId?: string;
  warehouseId?: string;
  createdByUserId?: string;
  profitabilityLines?: RealFiscalProfitabilityLineData[];
}

export interface RealFiscalProfitabilityLineData {
  saleItemId: string;
  productId: string;
  lotId: string;
  productName: string;
  productCode: string;
  uomCode: string;
  quantity: string;
  unitPriceMinor: number;
  lineTotalMinor: number;
  unitCostMinor: number;
  costTotalMinor: number;
  grossProfitMinor: number;
}

export interface RealFiscalSaleRegistryPageData {
  items: RealFiscalSaleRegistryItemData[];
  total: number;
  limit: number;
  offset: number;
}

export interface RealFiscalCorrectionSourceData extends RealFiscalSaleResultData {
  retailPointId: string;
  saleVersion: number;
  sourceShiftId: string;
  originalTotalMinor: number;
  correctionDraftEligible: boolean;
  correctionAllowed: boolean;
  correctionBlockedReason:
    "FISCAL_CORRECTION_TWO_STEP_CHAIN_REQUIRED";
  items: Array<{
    saleItemId: string;
    productId: string;
    lotId?: string | null;
    expiresOn?: string | null;
    quantity: string;
    unitPriceMinor: number;
    vatRate?: string | number | null;
  }>;
  payments: Array<{
    method: "cash" | "card" | "qr";
    amountMinor: number;
    status: string;
  }>;
  receiptChain: Array<{
    jobId: string;
    correctionId?: string;
    command: "receipt" | "correction";
    predecessorJobId?: string;
    jobStatus: string;
    statusVersion: number;
    replacementTotalMinor?: number;
    currentShiftId?: string;
    currentShiftStatus?: string;
    operationalEffectsApplied?: false;
    results: Array<{
      resultId: string;
      status: string;
      safeCode?: string | null;
      recordedAt: string;
      recoveryOfResultId?: string | null;
      executorCalled?: boolean | null;
    }>;
  }>;
}

export interface FiscalSaleRecoveryRecord {
  version: 1;
  submissionAttempted?: boolean;
  orderId: string;
  sourceDraftId: string;
  retailPointId: string;
  customerOrderVersion: number;
  expectedDraftVersion: number;
  command: { key: string; fingerprint: string };
  accepted?: {
    realSaleId: string;
    sourceDraftId: string;
    orderId: string;
    jobId: string;
  };
}

export const FISCAL_SALE_RECOVERY_STORAGE_KEY = "pharmhub:fiscal-sale-recovery";

export function createFiscalSaleRecoveryRecord(
  orderId: string,
  expectedDraftVersion: number,
  command: { key: string; fingerprint: string },
): FiscalSaleRecoveryRecord {
  const payload = parseFiscalInitiationFingerprint(command.fingerprint);
  if (!orderId.trim() || !command.key.trim() || !Number.isSafeInteger(expectedDraftVersion) || expectedDraftVersion <= 0) {
    throw new Error("FISCAL_RECOVERY_RECORD_INVALID");
  }
  return {
    version: 1,
    submissionAttempted: false,
    orderId,
    sourceDraftId: payload.sourceDraftId,
    retailPointId: payload.retailPointId,
    customerOrderVersion: payload.customerOrderVersion,
    expectedDraftVersion,
    command: { key: command.key, fingerprint: command.fingerprint },
  };
}

export function acceptFiscalSaleRecoveryRecord(
  record: FiscalSaleRecoveryRecord,
  accepted: RealFiscalSaleInitiationData,
): FiscalSaleRecoveryRecord {
  const payload = parseFiscalInitiationFingerprint(record.command.fingerprint);
  if (!accepted.realSaleId.trim()
    || !accepted.job?.body?.jobId?.trim()
    || accepted.sourceDraftId !== payload.sourceDraftId
    || accepted.orderId !== record.orderId) {
    throw new Error("FISCAL_RECOVERY_CONTEXT_MISMATCH");
  }
  return {
    ...record,
    accepted: {
      realSaleId: accepted.realSaleId,
      sourceDraftId: accepted.sourceDraftId,
      orderId: accepted.orderId,
      jobId: accepted.job.body.jobId,
    },
  };
}

export function parseFiscalSaleRecoveryRecord(raw: string): FiscalSaleRecoveryRecord {
  const value = JSON.parse(raw) as Partial<FiscalSaleRecoveryRecord>;
  if (value.version !== 1 || typeof value.orderId !== "string" || !value.orderId.trim()
    || (value.submissionAttempted !== undefined && typeof value.submissionAttempted !== "boolean")
    || typeof value.sourceDraftId !== "string" || !value.sourceDraftId.trim()
    || typeof value.retailPointId !== "string" || !value.retailPointId.trim()
    || !Number.isSafeInteger(value.customerOrderVersion) || Number(value.customerOrderVersion) <= 0
    || !Number.isSafeInteger(value.expectedDraftVersion) || Number(value.expectedDraftVersion) <= 0
    || !value.command || typeof value.command.key !== "string" || !value.command.key.trim()
    || typeof value.command.fingerprint !== "string") {
    throw new Error("FISCAL_RECOVERY_RECORD_INVALID");
  }
  const record = createFiscalSaleRecoveryRecord(value.orderId, Number(value.expectedDraftVersion), value.command);
  record.submissionAttempted = value.submissionAttempted ?? true;
  if (record.sourceDraftId !== value.sourceDraftId || record.retailPointId !== value.retailPointId
    || record.customerOrderVersion !== value.customerOrderVersion) throw new Error("FISCAL_RECOVERY_CONTEXT_MISMATCH");
  if (!value.accepted) return record;
  if (typeof value.accepted.realSaleId !== "string"
    || typeof value.accepted.sourceDraftId !== "string"
    || typeof value.accepted.orderId !== "string"
    || typeof value.accepted.jobId !== "string") {
    throw new Error("FISCAL_RECOVERY_RECORD_INVALID");
  }
  if (!value.accepted.realSaleId.trim() || !value.accepted.jobId.trim()
    || value.accepted.sourceDraftId !== record.sourceDraftId || value.accepted.orderId !== record.orderId) {
    throw new Error("FISCAL_RECOVERY_CONTEXT_MISMATCH");
  }
  return { ...record, accepted: value.accepted };
}

export function canReplayFrozenFiscalSubmission(
  recovery: FiscalSaleRecoveryRecord,
  currentRetailPointId?: string,
): boolean {
  return !recovery.accepted && Boolean(currentRetailPointId) && recovery.retailPointId === currentRetailPointId;
}

function parseFiscalInitiationFingerprint(fingerprint: string): RealFiscalSaleInitiationPayload {
  const payload = JSON.parse(fingerprint) as Partial<RealFiscalSaleInitiationPayload>;
  if (typeof payload.sourceDraftId !== "string" || !payload.sourceDraftId.trim()
    || typeof payload.retailPointId !== "string" || !payload.retailPointId.trim()
    || !Number.isSafeInteger(payload.customerOrderVersion) || Number(payload.customerOrderVersion) <= 0
    || !Array.isArray(payload.markingCodeHashes)
    || payload.markingCodeHashes.some((value) => typeof value !== "string")
    || (payload.externalNoncashConfirmed !== undefined && typeof payload.externalNoncashConfirmed !== "boolean")
    || !Array.isArray(payload.acquiringApprovals)
    || payload.acquiringApprovals.some((value) => !value || typeof value !== "object"
      || !realFiscalAcquiringApprovalReady(value as RealFiscalAcquiringApproval))) {
    throw new Error("FISCAL_RECOVERY_FINGERPRINT_INVALID");
  }
  return payload as RealFiscalSaleInitiationPayload;
}

export function parseRealFiscalSaleResult(value: unknown): RealFiscalSaleResultData {
  if (!value || typeof value !== "object") throw new Error("FISCAL_RESULT_INVALID");
  const candidate = value as Partial<RealFiscalSaleResultData>;
  const requiredStrings: Array<keyof RealFiscalSaleResultData> = [
    "realSaleId", "sourceDraftId", "orderId", "retailPointId", "jobId", "saleStatus", "jobStatus", "documentDate",
  ];
  if (requiredStrings.some((key) => typeof candidate[key] !== "string" || !(candidate[key] as string).trim())
    || !(["pending", "ready", "failed", "recovery_required"] as const).includes(candidate.state as RealFiscalSaleResultState)
    || typeof candidate.finalized !== "boolean"
    || typeof candidate.retryAllowed !== "boolean"
    || typeof candidate.noEffectResolved !== "boolean"
    || typeof candidate.resolutionRequired !== "boolean"
    || typeof candidate.freshDraftRequired !== "boolean"
    || (candidate.fiscalDocumentNumber != null && (!Number.isSafeInteger(candidate.fiscalDocumentNumber) || candidate.fiscalDocumentNumber <= 0))
    || (candidate.fiscalOccurredAt != null && typeof candidate.fiscalOccurredAt !== "string")
    || (candidate.safeCode != null && typeof candidate.safeCode !== "string")
    || (candidate.deliveryRequestStatus != null && typeof candidate.deliveryRequestStatus !== "string")
    || (candidate.noEffectResolved && (
      candidate.state !== "failed"
      || candidate.finalized
      || candidate.retryAllowed
      || candidate.resolutionRequired
      || !candidate.freshDraftRequired
    ))
    || (candidate.freshDraftRequired && !candidate.noEffectResolved)) {
    throw new Error("FISCAL_RESULT_INVALID");
  }
  return candidate as RealFiscalSaleResultData;
}

export function fiscalSaleFreshDraftAllowed(result: RealFiscalSaleResultData): boolean {
  return result.state === "failed"
    && result.noEffectResolved
    && !result.resolutionRequired
    && result.freshDraftRequired
    && !result.finalized
    && !result.retryAllowed;
}

export function fiscalSaleResultMatchesRecovery(
  result: RealFiscalSaleResultData,
  recovery: FiscalSaleRecoveryRecord,
): boolean {
  return Boolean(recovery.accepted)
    && result.realSaleId === recovery.accepted?.realSaleId
    && result.sourceDraftId === recovery.accepted?.sourceDraftId
    && result.orderId === recovery.accepted?.orderId
    && result.jobId === recovery.accepted?.jobId
    && result.retailPointId === recovery.retailPointId;
}

export interface FiscalSaleResultPresentation {
  tone: "progress" | "success" | "warning" | "error";
  title: string;
  detail: string;
}

export function fiscalSaleResultPresentation(result: RealFiscalSaleResultData): FiscalSaleResultPresentation {
  if (result.state === "pending") return {
    tone: "progress",
    title: "ККТ выполняет фискальную операцию",
    detail: "Оплата, печать и списание ещё не подтверждены. Новая команда заблокирована до результата сервера.",
  };
  if (result.state === "ready" && result.finalized) return {
    tone: "success",
    title: "Фискальный чек подтверждён сервером",
    detail: "Сервер завершил продажу, оплату и связанные товарные эффекты.",
  };
  if (result.state === "ready") return {
    tone: "warning",
    title: "Результат ККТ ещё не завершён сервером",
    detail: "Не создавайте новую продажу: требуется завершение серверных эффектов и повторная сверка статуса.",
  };
  if (fiscalSaleFreshDraftAllowed(result)) return {
    tone: "warning",
    title: "Операция завершена без фискального чека",
    detail: result.safeCode === "COMMAND_EXPIRED_NO_EXECUTION"
      ? "Срок команды истёк до её передачи кассовому агенту. Чек не создан, деньги и товар не списаны. Старую команду повторять нельзя; начните новую продажу по исходному заказу."
      : "Администратор подтвердил отсутствие эффекта ККТ. Старую команду повторять нельзя; начните новую продажу по исходному заказу.",
  };
  const safeReason = fiscalSafeBlockerLabel(result.safeCode || (result.state === "recovery_required" ? "FISCAL_RECOVERY_REQUIRED" : "DEFINITIVE_FAILED_RECEIPT_RESOLUTION_REQUIRED"));
  return result.state === "recovery_required" ? {
    tone: "error",
    title: "Результат чека требует сверки",
    detail: `${safeReason}. Новая фискальная команда заблокирована; решение выполняет администратор после сверки ККТ.`,
  } : {
    tone: "error",
    title: "Фискальная операция не завершена",
    detail: `${safeReason}. Автоматический повтор запрещён; решение выполняет администратор по журналу ККТ.`,
  };
}

export function startFiscalSaleResultPolling(options: {
  realSaleId: string;
  fetchResult: (realSaleId: string, signal: AbortSignal) => Promise<RealFiscalSaleResultData>;
  onResult: (result: RealFiscalSaleResultData) => void;
  onError: (error: unknown) => void;
  schedule: (callback: () => void, delayMs: number) => unknown;
  cancelSchedule: (handle: unknown) => void;
  intervalMs?: number;
  recoveryIntervalMs?: number;
  maxErrorIntervalMs?: number;
}): () => void {
  let stopped = false;
  let scheduled: unknown;
  let controller: AbortController | undefined;
  let consecutiveErrors = 0;
  const scheduleNext = (delayMs: number) => {
    scheduled = options.schedule(() => {
      scheduled = undefined;
      void poll();
    }, delayMs);
  };
  const poll = async () => {
    if (stopped) return;
    controller = new AbortController();
    try {
      const result = await options.fetchResult(options.realSaleId, controller.signal);
      if (stopped) return;
      consecutiveErrors = 0;
      options.onResult(result);
      if (!(result.state === "ready" && result.finalized) && !fiscalSaleFreshDraftAllowed(result)) {
        // Recovery remains blocked; periodic GET can observe an administrator's resolution.
        scheduleNext(result.state === "pending" || result.state === "ready"
          ? options.intervalMs ?? 1500
          : options.recoveryIntervalMs ?? 30_000);
      }
    } catch (error) {
      if (!stopped && !(error && typeof error === "object" && "name" in error && error.name === "AbortError")) {
        options.onError(error);
        consecutiveErrors += 1;
        scheduleNext(Math.min(options.maxErrorIntervalMs ?? 12_000,
          (options.intervalMs ?? 1500) * (2 ** Math.min(consecutiveErrors - 1, 16))));
      }
    }
  };
  void poll();
  return () => {
    stopped = true;
    if (scheduled !== undefined) options.cancelSchedule(scheduled);
    controller?.abort();
  };
}

export interface RealFiscalSaleInitiationPayload {
  sourceDraftId: string;
  retailPointId: string;
  customerOrderVersion: number;
  clientSafetyAcknowledgementId?: string;
  markingCodeHashes: string[];
  acquiringApprovals: RealFiscalAcquiringApproval[];
  externalNoncashConfirmed?: boolean;
}

export interface RealFiscalAcquiringApproval {
  method: "card" | "qr";
  amountMinor: number;
  approvalReference: string;
  occurredAt: string;
}

export function realFiscalAcquiringApprovalReady(
  value: RealFiscalAcquiringApproval,
): boolean {
  const reference = value.approvalReference;
  const digits = reference.replace(/[^0-9]/g, "");
  return (value.method === "card" || value.method === "qr")
    && Number.isSafeInteger(value.amountMinor)
    && value.amountMinor > 0
    && reference.length >= 4
    && reference.length <= 120
    && reference === reference.trim()
    && !/[\u0000-\u001f\u007f]/.test(reference)
    && !(digits.length >= 12 && digits.length <= 19)
    && Number.isFinite(Date.parse(value.occurredAt));
}

export function buildRealFiscalSaleInitiationPayload(value: {
  sourceDraftId: string;
  retailPointId: string;
  customerOrderVersion: number;
  clientSafetyAcknowledgementId?: string;
  acquiringApprovals?: RealFiscalAcquiringApproval[];
  externalNoncashConfirmed?: boolean;
}): RealFiscalSaleInitiationPayload {
  return {
    sourceDraftId: value.sourceDraftId,
    retailPointId: value.retailPointId,
    customerOrderVersion: value.customerOrderVersion,
    ...(value.clientSafetyAcknowledgementId ? { clientSafetyAcknowledgementId: value.clientSafetyAcknowledgementId } : {}),
    markingCodeHashes: [],
    acquiringApprovals: value.acquiringApprovals ?? [],
    ...(value.externalNoncashConfirmed === true ? { externalNoncashConfirmed: true } : {}),
  };
}

export async function buildAcknowledgedRealFiscalSaleInitiationPayload(value: {
  sourceDraftId: string;
  retailPointId: string;
  customerOrderVersion: number;
  acquiringApprovals?: RealFiscalAcquiringApproval[];
  externalNoncashConfirmed?: boolean;
  ensureClientSafetyAcknowledgement: () => Promise<string | undefined>;
}): Promise<RealFiscalSaleInitiationPayload> {
  const clientSafetyAcknowledgementId = await value.ensureClientSafetyAcknowledgement();
  return buildRealFiscalSaleInitiationPayload({
    sourceDraftId: value.sourceDraftId,
    retailPointId: value.retailPointId,
    customerOrderVersion: value.customerOrderVersion,
    clientSafetyAcknowledgementId,
    acquiringApprovals: value.acquiringApprovals,
    externalNoncashConfirmed: value.externalNoncashConfirmed,
  });
}

export function isCashOnlyFiscalDraft(draft: Pick<RetailSaleDraftData, "paymentMethod" | "paymentAllocations" | "totalMinor">): boolean {
  return draft.paymentMethod === "cash"
    && draft.paymentAllocations.length === 1
    && draft.paymentAllocations[0]?.method === "cash"
    && draft.paymentAllocations[0].amountMinor === draft.totalMinor;
}

export function externalNoncashPaymentAllowed(capability: FiscalCapabilityData | null | undefined, fulfilmentMode: string | undefined, method: string): boolean {
  const local = fulfilmentMode === "pickup" || fulfilmentMode === "pharmacy";
  const remote = capability?.remoteNoncashEnabled === true
    && (fulfilmentMode === "delivery" || fulfilmentMode === "shipping");
  return (local || remote)
    && (fulfilmentMode !== "shipping" || method === "card")
    && capability?.noncashConfirmationMode === "manual_external_received"
    && (method === "card" || method === "mixed")
    && Array.isArray(capability.allowedPaymentMethods)
    && capability.allowedPaymentMethods.includes(method);
}

/** Empty/invalid input cannot silently turn into cash=0; keep exact kopecks. */
export function mixedExternalPaymentParts(input: string, totalMinor: number): { cash: number; card: number } | null {
  if (!Number.isSafeInteger(totalMinor) || totalMinor <= 0 || !/^\d+(?:[,.]\d{1,2})?$/.test(input.trim())) return null;
  const [whole, cents = ""] = input.trim().replace(",", ".").split(".");
  const cashBig = BigInt(whole!) * 100n + BigInt(cents.padEnd(2, "0"));
  if (cashBig <= 0n || cashBig >= BigInt(totalMinor)) return null;
  const cash = Number(cashBig);
  return { cash, card: totalMinor - cash };
}

export function realFiscalClientBlocker(value: {
  accepted: boolean;
  capabilityEnabled: boolean;
  pointReady: boolean;
  linkedOrderId?: string;
  customerOrderVersion?: number;
  draft?: Pick<RetailSaleDraftData, "customerOrderId" | "status" | "sandbox" | "paymentMethod" | "paymentAllocations" | "totalMinor">;
  exactFullOrder: boolean;
  acquiringApprovals?: RealFiscalAcquiringApproval[];
  paymentCapability?: FiscalCapabilityData | null;
  fulfilmentMode?: string;
  externalNoncashConfirmed?: boolean;
}): string | undefined {
  if (value.accepted) return "FISCAL_COMMAND_ALREADY_ACCEPTED";
  if (!value.pointReady) return "FISCAL_DEVICE_READINESS_UNAVAILABLE";
  if (!value.capabilityEnabled) return "FISCAL_CAPABILITY_UNAVAILABLE";
  if (!value.linkedOrderId) return "FISCAL_LINKED_ORDER_REQUIRED";
  if (!Number.isSafeInteger(value.customerOrderVersion) || Number(value.customerOrderVersion) <= 0) return "FISCAL_ORDER_VERSION_REQUIRED";
  if (!value.draft) return "FISCAL_SAVED_DRAFT_REQUIRED";
  if (value.draft.customerOrderId !== value.linkedOrderId || value.draft.status !== "draft" || !value.draft.sandbox) return "FISCAL_ELIGIBLE_OPERATIONAL_DRAFT_REQUIRED";
  if (!value.exactFullOrder) return "FISCAL_EXACT_FULL_ORDER_REQUIRED";
  const noncash = value.draft.paymentAllocations.filter((item) => item.method === "card" || item.method === "qr");
  const approvals = value.acquiringApprovals ?? [];
  if (value.draft.paymentAllocations.some((item) => item.method === "prepayment")) return "PREPAYMENT_REAL_LEDGER_EVIDENCE";
  if (value.fulfilmentMode === "shipping" && (
    value.draft.paymentMethod !== "card"
    || value.draft.paymentAllocations.some((item) => item.method !== "card")
  )) return "SHIPPING_NONCASH_PAYMENT_REQUIRED";
  if (noncash.length > 0) {
    if (!externalNoncashPaymentAllowed(value.paymentCapability, value.fulfilmentMode, value.draft.paymentMethod)
      || noncash.some((part) => part.method !== "card")) return "ACQUIRING_EXTERNAL_GATE_CLOSED";
    const allocations = value.draft.paymentAllocations;
    if (!Number.isSafeInteger(value.draft.totalMinor) || value.draft.totalMinor <= 0
      || allocations.some((part) => !Number.isSafeInteger(part.amountMinor) || part.amountMinor <= 0)
      || new Set(allocations.map((part) => part.method)).size !== allocations.length
      || allocations.reduce((sum, part) => sum + BigInt(part.amountMinor), 0n) !== BigInt(value.draft.totalMinor)
      || (value.draft.paymentMethod === "card" && (allocations.length !== 1 || allocations[0]?.method !== "card"))
      || (value.draft.paymentMethod === "mixed" && (allocations.length !== 2 || !allocations.some((part) => part.method === "cash")))) return "FISCAL_PAYMENT_ALLOCATION_INVALID";
    if (!value.externalNoncashConfirmed) return "EXTERNAL_NONCASH_CONFIRMATION_REQUIRED";
    if (approvals.length) return "ACQUIRING_EXTERNAL_GATE_CLOSED";
    return undefined;
  }
  if (
    noncash.length !== approvals.length
    || approvals.some((approval) => !realFiscalAcquiringApprovalReady(approval))
    || noncash.some((item) => !approvals.some((approval) => approval.method === item.method && approval.amountMinor === item.amountMinor))
  ) return "ACQUIRING_PAYMENT_EVIDENCE";
  return undefined;
}

export function fiscalSafeBlockerLabel(value?: string): string {
  const labels: Record<string, string> = {
    SHIPPING_NONCASH_PAYMENT_REQUIRED: "Для отправки требуется полностью безналичная оплата. Наличные управляющему не начисляются.",
    SERVER_EXECUTION_FUSE_CLOSED: "Фискальный контур выключен на сервере",
    FISCAL_RUN_GATE_CLOSED: "Безопасное окно реальной фискализации закрыто на сервере",
    FISCAL_SALE_ORCHESTRATION_BLOCKED: "Фискальная продажа заблокирована до подключения всех защищённых контрактов",
    FISCAL_SERVER_WIRING_UNAVAILABLE: "Фискальный сервер не настроен",
    FISCAL_CAPABILITY_UNAVAILABLE: "Сервер не подтвердил готовность фискального контура",
    FISCAL_DEVICE_READINESS_UNAVAILABLE: "ККТ или её защищённый шлюз не подтвердили готовность",
    FISCAL_SHIFT_EXPIRED: "Срок смены ККТ истёк. Отмените подготовку чека, закройте текущую смену и откройте новую.",
    FISCAL_SHIFT_CLOSED: "Смена ККТ закрыта. Откройте новую смену перед печатью чека.",
    DEVICE_PAYLOAD_ENCRYPTION_KEY: "Ключ шифрования задания для ККТ не настроен",
    FISCAL_ORDER_EVENT_ENCRYPTION_UNAVAILABLE: "Защищённый журнал событий заказа не готов",
    FISCAL_RECOVERY_REQUIRED: "ККТ требует сверки: новый чек заблокирован до установления результата",
    COMMAND_EXPIRED_NO_EXECUTION: "Срок команды истёк до её передачи кассовому агенту; физическая операция не выполнялась",
    FISCAL_MEASUREMENT_UNIT_UNSUPPORTED: "В фискальном профиле товара указана неподдерживаемая единица измерения. Администратору нужно проверить настройки товара. Чек в ККТ не отправлен.",
    DEFINITIVE_FAILED_RECEIPT_RESOLUTION_REQUIRED: "Неуспешный результат чека требует ручного решения администратора",
    FISCAL_COMMAND_CONFLICT: "Фискальная команда уже изменена; обновите данные перед следующим действием",
    MARKED_STOCK_COMMIT_GATE: "Маркированный товар заблокирован до отдельной проверки маркировки",
    SHIPPING_REAL_FISCAL_WORKFLOW_UNAVAILABLE: "Фискализация доставки и почты в этом предрелизе заблокирована",
    ACQUIRING_EXTERNAL_GATE_CLOSED: "Сервер не разрешил этот способ безналичной оплаты. Интеграция с банком не подключена",
    EXTERNAL_NONCASH_CONFIRMATION_REQUIRED: "Подтвердите, что безналичная часть уже оплачена отдельно",
    FISCAL_PAYMENT_ALLOCATION_INVALID: "Части оплаты не совпадают с суммой чека",
    ACQUIRING_PAYMENT_EVIDENCE: "Для безналичной части нужно точное подтверждение терминала или банка без реквизитов карты",
    PREPAYMENT_REAL_LEDGER_EVIDENCE: "Реальный зачёт предоплаты не подтверждён постоянным реестром",
    DURABLE_FISCAL_SHIFT_BINDING: "Фискальная смена не привязана к операционной смене",
    DURABLE_POS_SALE_PAYMENT_UOW: "Единая транзакция продажи и оплаты не готова",
    CLIENT_SAFETY_REVALIDATION_REQUIRED: "Статус клиента изменился; повторите проверку клиента",
    FISCAL_LINKED_ORDER_REQUIRED: "Реальная фискализация доступна только из связанного готового заказа",
    FISCAL_ORDER_VERSION_REQUIRED: "Обновите заказ: сервер не подтвердил его версию",
    FISCAL_SAVED_DRAFT_REQUIRED: "Проверьте и сохраните операционный черновик продажи",
    FISCAL_ELIGIBLE_OPERATIONAL_DRAFT_REQUIRED: "Для реальной ККТ нужен неизменённый операционный черновик связанного заказа",
    FISCAL_EXACT_FULL_ORDER_REQUIRED: "Реальная фискализация требует весь заказ без частичной продажи",
    FISCAL_COMMAND_ALREADY_ACCEPTED: "Фискальное задание уже принято; повторный запуск заблокирован",
  };
  return labels[value ?? ""] ?? "Фискальная операция безопасно заблокирована сервером";
}

export function fiscalCapabilityBlockerLabels(capability?: FiscalCapabilityData | null): string[] {
  if (!capability) return [fiscalSafeBlockerLabel("FISCAL_CAPABILITY_UNAVAILABLE")];
  const codes = capability.missingContracts?.length ? capability.missingContracts : [capability.safeCode];
  return [...new Set(codes.map(fiscalSafeBlockerLabel))];
}

export interface RetailSaleReturnData {
  id: string;
  saleId: string;
  shiftId: string;
  paymentMethod: "cash" | "card" | "qr";
  amountMinor: number;
  status: "posted";
  reason: string;
  version: number;
  documentDate: string;
  returnedAt: string;
  fiscalEligible: false;
  sandbox: true;
}

export interface CommodityReceiptData {
  id: string;
  number: string;
  saleId: string;
  status: "pending" | "rendered";
  copyCount: number;
  version: number;
  templateVersion: string;
  sourceKind: "operational_sandbox";
  fiscalized: false;
  immutable: true;
  snapshotSha256: string;
  pdfSha256?: string;
  size?: number;
  createdAt: string;
  renderedAt?: string;
  pdfUrl?: string;
}

export interface CommodityReceiptCopyData {
  receiptId: string;
  saleId: string;
  number: string;
  copyNumber: number;
  templateVersion: string;
  sourceKind: "operational_sandbox";
  fiscalized: false;
  immutable: true;
  pdfSha256: string;
  size: number;
  createdAt: string;
  pdfUrl: string;
}

export interface RetailShiftPageData {
  items: RetailShiftData[];
  total: number;
  offset: number;
  limit: number;
}

export interface RetailSaleDraftPageData {
  items: RetailSaleDraftData[];
  total: number;
  offset: number;
  limit: number;
}

export interface RetailShiftOperationsPageData {
  shift: RetailShiftData;
  items: RetailSaleDraftData[];
  total: number;
  offset: number;
  limit: number;
  completedCount: number;
  draftCount: number;
  cashMinor: number;
  cardMinor: number;
  qrMinor: number;
  prepaymentMinor: number;
  paidInShiftMinor: number;
  calculationTotalMinor: number;
  dailyCashReport?: {
    state: "pending" | "available";
    businessDate: string;
    availableAt: string;
    reportId?: string;
    deepLink: string;
  };
  sourceKind: "operational_sandbox";
  previewComplete: boolean;
  submissionAvailable: boolean;
  blockerCode: "persistent_shift_report_unavailable";
  submissionBlocker: string;
  shiftReport?: ShiftReportData;
}

export interface CashHandoverData {
  id: string;
  number: string;
  pharmacyUserId: string;
  operatorUserId: string;
  shiftId: string;
  pharmacyName?: string;
  operatorName?: string;
  shiftLabel?: string;
  amountMinor: number;
  actualAmountMinor?: number;
  creditedAmountMinor?: number;
  reason: string;
  note?: string;
  status: "draft" | "issued_by_pharmacy" | "disputed" | "completed" | "cancelled";
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  issuedAt?: string;
  completedAt?: string;
  sandbox: boolean;
}

export interface CashHandoverPageData { items: CashHandoverData[]; total: number }

export interface OperatorCashSummaryData {
  operatorUserId: string;
  balanceMinor: number;
  pendingIncomingMinor: number;
  disputedMinor: number;
  completedHandovers: number;
  deliveryCashMinor: number;
  deliveryCredits: number;
  sandbox: boolean;
}

export type CompanyCashCategory =
  | "bank_withdrawal"
  | "bank_deposit"
  | "supplier_payment"
  | "service_income"
  | "service_payment"
  | "other_income"
  | "other_expense";

export interface CompanyCashMovementData {
  id: string;
  number: string;
  operatorUserId: string;
  direction: "incoming" | "outgoing";
  category: CompanyCashCategory;
  counterparty: string;
  amountMinor: number;
  comment?: string;
  status: "posted" | "cancelled";
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
  cancellationReason?: string;
  sandbox: boolean;
}

export interface CompanyCashMovementPageData { items: CompanyCashMovementData[]; total: number }

export interface CompanyCashSummaryData {
  operatorUserId: string;
  pharmacyHandoversMinor: number;
  deliveryCashMinor: number;
  manualIncomingMinor: number;
  manualOutgoingMinor: number;
  balanceMinor: number;
  activeOperations: number;
  sandbox: boolean;
}

export interface OperatorCashReconciliationData {
  id: string;
  number: string;
  operatorUserId: string;
  expectedBalanceMinor: number;
  actualBalanceMinor: number;
  differenceMinor: number;
  status: "pending" | "confirmed" | "rejected";
  version: number;
  cutoffAt: string;
  comment?: string;
  decisionReason?: string;
  createdAt: string;
  updatedAt: string;
  decidedAt?: string;
  sandbox: boolean;
}

export interface OperatorCashReconciliationPageData {
  items: OperatorCashReconciliationData[];
  total: number;
}

export interface DeliveryLiabilityData {
  id: string;
  number: string;
  orderId: string;
  taskId: string;
  saleId: string;
  fiscalJobId: string;
  operatorUserId: string;
  pharmacyUserId: string;
  retailPointId: string;
  linkedOperationLabel: string;
  paymentMethod: "cash" | "card" | "qr";
  expectedAmountMinor: number;
  expectedCashResponsibilityMinor: number;
  actualAmountMinor?: number;
  discrepancyMinor?: number;
  creditedCashMinor: number;
  status: "draft" | "issued_to_operator" | "delivered_pending_cash" | "cash_confirmed" | "disputed" | "resolved" | "cancelled";
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
  comment?: string;
  sandbox: boolean;
}

export interface DeliveryLiabilityPageData { items: DeliveryLiabilityData[]; total: number }

export interface FinanceOverviewData {
  cashIncoming: { documents: number; amountMinor: number };
  cashOutgoing: { documents: number; amountMinor: number };
  bankIncoming: { documents: number; amountMinor: number };
  bankOutgoing: { documents: number; amountMinor: number };
  retailCashIncoming: { documents: number; amountMinor: number };
  retailCashOutgoing: { documents: number; amountMinor: number };
  supplierDebtMinor?: number;
  openSupplierObligations?: number;
  source: "moysklad_audit" | "postgres";
}

export interface LegalEntityOptionData { id: string; name: string }

export interface PaymentData {
  id: string;
  number: string;
  legalEntityId: string;
  legalEntityName: string;
  supplierId?: string;
  supplierName?: string;
  direction: "incoming" | "outgoing";
  method: "cash" | "bank" | "card" | "qr" | "mixed";
  status: "draft" | "posted" | "reversed";
  amountMinor: number;
  article: string;
  sourceReceiptId?: string;
  sourceReceiptNumber?: string;
  sourcePurchaseOrderId?: string;
  sourcePurchaseOrderNumber?: string;
  documentDate: string;
  occurredAt: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  postedAt?: string;
  reversedAt?: string;
  reversalReason?: string;
  sandbox: boolean;
}

export interface PaymentPageData { items: PaymentData[]; total: number; sandbox: boolean }

export interface SupplierObligationData {
  receiptId: string;
  receiptNumber: string;
  legalEntityId: string;
  legalEntityName: string;
  supplierId: string;
  supplierName: string;
  originalMinor: number;
  returnedMinor: number;
  paidMinor: number;
  outstandingMinor: number;
  status: "open" | "paid" | "credited";
  documentDate: string;
}

export interface SupplierPaymentSettlementData {
  paymentId: string;
  paymentNumber: string;
  legalEntityId: string;
  legalEntityName: string;
  supplierId: string;
  supplierName: string;
  amountMinor: number;
  documentDate: string;
}

export interface SupplierSettlementMovementData {
  kind: "receipt" | "return" | "payment" | "adjustment";
  documentId: string;
  documentNumber: string;
  sourceReceiptId?: string | null;
  sourceReceiptNumber?: string;
  documentDate: string;
  debitMinor: number;
  creditMinor: number;
  runningDebtMinor: number;
  runningPrepaymentMinor: number;
}

export interface SupplierSettlementData {
  legalEntityId: string;
  legalEntityName: string;
  supplierId: string;
  supplierName: string;
  originalMinor: number;
  returnedMinor: number;
  paidMinor: number;
  adjustmentMinor?: number;
  openingDebtMinor: number;
  openingPrepaymentMinor: number;
  periodDebitMinor: number;
  periodCreditMinor: number;
  outstandingMinor: number;
  prepaymentMinor: number;
  obligations: SupplierObligationData[];
  payments: SupplierPaymentSettlementData[];
  movements: SupplierSettlementMovementData[];
}

export interface SupplierSettlementPageData {
  items: SupplierSettlementData[];
  periodFrom?: string;
  periodTo?: string;
  totalOpeningDebtMinor: number;
  totalOpeningPrepaymentMinor: number;
  totalDebtMinor: number;
  totalPrepaymentMinor: number;
  openObligations: number;
  sandbox: boolean;
}

export interface TaskViewData {
  id: string;
  customerOrderId?: string;
  customerOrderNumber?: string;
  taskType: string;
  status: string;
  priority: string;
  authorUserId: string;
  assigneeUserId: string;
  assigneeDisplayName: string;
  dueAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  products: Array<{ productId: string; productName: string; requestedQuantity: string; completedQuantity: string }>;
  linkedOrder?: {
    id: string;
    number: string;
    customerLabel: string;
    customerPhone?: string;
    requestedFor?: string;
    deliveryAddress?: string;
    workflowStatus: "new"|"confirmed"|"reserved"|"assigned"|"picking"|"ready"|"fulfilled"|"closed"|"cancelled";
    fulfilmentMode: "pharmacy"|"pickup"|"delivery"|"shipping";
    version: number;
    items: Array<{
      productId: string;
      productName: string;
      quantity: string;
      reservedQuantity: string;
      fulfilledQuantity: string;
      trackingType: string;
      scannedPackages: number;
      lots: Array<{ lotId: string; expiresOn: string | null; quantity: string }>;
    }>;
  };
}

/** One user-facing execution document. Stock movement stays a technical
 * backend side effect and is never exposed as another editable document. */
export interface ExecutionRequestLineData {
  productId: string;
  productName: string;
  lotId: string;
  lotLabel: string;
  expiresOn: string | null;
  quantity: string;
  receivedQuantity: string;
}

export interface ExecutionRequestReceiptObservationData {
  productId: string;
  lotId: string;
  observedQuantity: string;
  observedExpiresOn: string | null;
  observedLotLabel: string;
  acceptedIdentity: boolean;
  creditedQuantity: string;
  issueCodes: string[];
}

export interface ExecutionRequestReceiptAttemptData {
  kind: "pharmacy_receipt" | "late_receipt";
  actorDisplayName: string;
  lines: ExecutionRequestReceiptObservationData[];
  recordedAt: string;
}

export type ExecutionRequestReceiptResolutionAction =
  | "record_late_receipt"
  | "acknowledge_quarantine"
  | "accept_shortage";

export interface ExecutionRequestReceiptResolutionData {
  action: ExecutionRequestReceiptResolutionAction | "confirm_unknown_expiry";
  reason?: string;
  actorDisplayName: string;
  attemptCount: number;
  recordedAt: string;
}

export interface ExecutionRequestActualQuantity {
  productId: string;
  lotId: string;
  quantity: string;
  observedExpiresOn: string | null;
  observedLotLabel: string;
}

export interface ExecutionRequestReceiptFormLine {
  quantity: string;
  expiryMonth: string;
  lotLabel: string;
}

export interface ExecutionRequestData {
  id: string;
  number: string;
  orderId: string;
  orderNumber: string;
  displayKind: "execution_request" | "pharmacy_receipt";
  displayLabel: "Заявка управляющему";
  route: "to_pharmacy" | "delivery" | "shipping";
  sourceWarehouseId: string;
  sourceWarehouseName: string;
  targetWarehouseId?: string;
  targetWarehouseName?: string;
  assigneeUserId: string;
  assigneeDisplayName: string;
  requiredAt?: string;
  status: "new" | "accepted" | "in_transit" | "pharmacy_received" | "completed" | "cancelled";
  pharmacyReceiptStatus: "not_applicable" | "awaiting" | "received" | "discrepancy" | "resolved_discrepancy";
  /** Role- and scope-filtered commands supplied by the API for this projection. */
  availableActions?: ExecutionRequestTransitionEvent[];
  /** Server-owned fiscal/KKT gate. Missing is deliberately treated as false. */
  finalHandoffAvailable?: boolean;
  /** Safe, role-scoped blocker code supplied by the API; never display it verbatim. */
  finalHandoffBlocker?: string;
  posOrderId?: string;
  posLaunchAvailable?: boolean;
  receiptReconciliationStatus?: "not_applicable" | "pending" | "ready_for_pos" | "reconciled";
  lines: ExecutionRequestLineData[];
  receiptAttempts: ExecutionRequestReceiptAttemptData[];
  receiptResolutions: ExecutionRequestReceiptResolutionData[];
  comment?: string;
  contactName?: string;
  contactPhone?: string;
  deliveryAddress?: string;
  postalCode?: string;
  carrier?: string;
  trackingNumber?: string;
  shippedAt?: string;
  postalExpenseMinor?: number;
  paymentMode?: "cash" | "noncash" | "partial";
  cashMinor?: number;
  noncashMinor?: number;
  version: number;
  documentDate: string;
  createdAt: string;
  updatedAt: string;
}

export type ExecutionRequestTransitionEvent = "accept" | "transfer_to_pharmacy" | "receive" | "complete" | "resolve_receipt";

export interface ExecutionRequestAction {
  event: ExecutionRequestTransitionEvent;
  label: string;
  disabled?: boolean;
  reason?: string;
}

const finalHandoffBlockerMessages: Record<string, string> = {
  not_applicable: "Финальная передача неприменима для этого маршрута",
  pharmacy_receipt_discrepancy: "В приёмке аптеки есть расхождение. Сначала урегулируйте его",
  pharmacy_receipt_required: "Аптека ещё не подтвердила полный приход товара",
  receipt_reconciliation_required: "Сначала сформируйте чек заказа в POS; затем сервер должен подтвердить согласованное состояние чека и оплаты",
  fiscal_delivery_workflow_unavailable: "Фискальный контур доставки ещё не реализован. Финальная передача заблокирована",
};

const genericFinalHandoffBlocker = "Сервер не подтвердил готовность финальной передачи";

export function executionRequestFinalHandoffBlockerMessage(code?: string, route?: ExecutionRequestData["route"]): string {
  if (!code) return genericFinalHandoffBlocker;
  const normalized = code.trim();
  if (route === "delivery" && (normalized === "pharmacy_receipt_discrepancy" || normalized === "pharmacy_receipt_required")) {
    return genericFinalHandoffBlocker;
  }
  return finalHandoffBlockerMessages[normalized] ?? genericFinalHandoffBlocker;
}

export function executionRequestPresentsPharmacyReceipt(
  request: Pick<ExecutionRequestData, "route" | "displayKind">,
): boolean {
  return request.route !== "delivery" && request.displayKind === "pharmacy_receipt";
}

/**
 * Mirrors the server lifecycle without attempting to prove authorization or
 * fiscal readiness in the browser. The API remains authoritative for scope,
 * assignment and every transition.
 */
export function executionRequestActionFor(role: Role, request: ExecutionRequestData): ExecutionRequestAction | null {
  const available = new Set(request.availableActions ?? []);
  const canOperate = role === "admin" || role === "operator";
  if (canOperate) {
    if (request.status === "new" && available.has("accept")) return { event: "accept", label: "Принять" };
    if (request.route !== "delivery" && request.status === "accepted" && available.has("transfer_to_pharmacy")) {
      return { event: "transfer_to_pharmacy", label: "Передать в аптеку" };
    }
    if (available.has("complete")) {
      return { event: "complete", label: request.route === "delivery" ? "Подтвердить доставку" : "Завершить" };
    }
  }
  if (request.route !== "delivery" && (role === "admin" || role === "pharmacy") && request.status === "in_transit" && request.pharmacyReceiptStatus === "awaiting" && available.has("receive")) {
    return { event: "receive", label: "Подтвердить приход" };
  }
  if (request.route !== "delivery" && role === "admin" && request.pharmacyReceiptStatus === "discrepancy" && available.has("resolve_receipt")) {
    return { event: "resolve_receipt", label: "Урегулировать расхождение" };
  }
  return null;
}

const executionRequestTransitionEvents: ExecutionRequestTransitionEvent[] = [
  "accept", "transfer_to_pharmacy", "receive", "complete", "resolve_receipt",
];

/** Unknown server actions are ignored; the browser never invents a command. */
export function executionRequestAvailableActions(value: unknown): ExecutionRequestTransitionEvent[] {
  if (!Array.isArray(value)) return [];
  return value.filter((action): action is ExecutionRequestTransitionEvent =>
    typeof action === "string" && executionRequestTransitionEvents.includes(action as ExecutionRequestTransitionEvent));
}

export function executionRequestTransitionBody(
  event: ExecutionRequestTransitionEvent,
  actualQuantities: readonly ExecutionRequestActualQuantity[] = [],
  resolution?: { action: ExecutionRequestReceiptResolutionAction; reason: string },
  completion?: {
    carrier?: string; trackingNumber?: string; shippedAt?: string;
    postalExpenseMinor?: number; paymentMode: "cash" | "noncash" | "partial";
    cashMinor: number;
  },
): Record<string, unknown> {
  const observations = actualQuantities.map((value) => ({ ...value }));
  if (event === "receive") return { event, actualQuantities: observations };
  if (event === "resolve_receipt") {
    if (!resolution) throw new Error("Выберите решение и укажите основание");
    return {
      event,
      resolutionAction: resolution.action,
      resolutionReason: resolution.reason,
      actualQuantities: resolution.action === "record_late_receipt" ? observations : [],
    };
  }
  if (event === "complete") {
    if (!completion) throw new Error("Укажите точное распределение оплаты");
    return { event, ...completion };
  }
  return { event };
}

const receiptIssueLabels: Record<string, string> = {
  shortage: "Недостача: фактически принято меньше запланированного",
  missing: "Недостача: товар фактически не получен",
  overage: "Излишек: количество сверх плана не зачислено в доступный остаток",
  expiry_mismatch: "Срок не совпадает: товар с другим сроком остаётся недоступным",
  lot_label_mismatch: "Партия не совпадает: товар другой партии остаётся недоступным",
  unexpected_identity: "Неожиданный товар или партия: позиция остаётся недоступной",
};

export function executionRequestReceiptIssueLabel(code: string): string {
  return receiptIssueLabels[code] ?? "Неизвестное расхождение: товар остаётся недоступным";
}

export function executionRequestReceiptDraftChanged(
  current: Record<string, ExecutionRequestReceiptFormLine>,
  baseline: Record<string, ExecutionRequestReceiptFormLine>,
): boolean {
  const keys = Object.keys(current);
  if (keys.length !== Object.keys(baseline).length) return true;
  return keys.some((key) => {
    const value = current[key];
    const initial = baseline[key];
    return !value || !initial
      || value.quantity !== initial.quantity
      || value.expiryMonth !== initial.expiryMonth
      || value.lotLabel !== initial.lotLabel;
  });
}

export function executionRequestReceiptObservationSummary(
  observation: Pick<ExecutionRequestReceiptObservationData, "observedQuantity" | "creditedQuantity" | "acceptedIdentity" | "issueCodes">,
  plannedQuantity: string | undefined,
  attemptKind: ExecutionRequestReceiptAttemptData["kind"],
  receiptStatus: ExecutionRequestData["pharmacyReceiptStatus"],
): string {
  if (observation.issueCodes.length) return observation.issueCodes.map(executionRequestReceiptIssueLabel).join(" · ");
  if (!observation.acceptedIdentity) return executionRequestReceiptIssueLabel("unknown");
  if (attemptKind === "pharmacy_receipt" && plannedQuantity !== undefined && (
    Number(observation.observedQuantity) < Number(plannedQuantity)
    || Number(observation.creditedQuantity) < Number(plannedQuantity)
  )) {
    return `Недостача: наблюдалось ${observation.observedQuantity}, зачислено ${observation.creditedQuantity} из ${plannedQuantity}`;
  }
  if (receiptStatus === "discrepancy" || receiptStatus === "resolved_discrepancy") {
    return "Идентичность подтверждена; итог расхождения рассчитан по всем попыткам";
  }
  return "Расхождений этой строки нет";
}

export function executionRequestTransitionFingerprint(
  request: Pick<ExecutionRequestData, "id" | "version">,
  event: ExecutionRequestTransitionEvent,
  body: unknown,
): string {
  return JSON.stringify({ id: request.id, version: request.version, event, body });
}

/** Keep the same command key while an identical transition has an uncertain result. */
export function stableExecutionRequestCommandKey(
  cache: Map<string, string>,
  fingerprint: string,
  create: () => string = () => crypto.randomUUID(),
): string {
  const existing = cache.get(fingerprint);
  if (existing) return existing;
  const created = create();
  cache.set(fingerprint, created);
  return created;
}

export interface ExecutionRequestPageData {
  items: ExecutionRequestData[];
  total: number;
  offset: number;
  limit: number;
}

export type OrderDetailsRequestLookupState = "loading" | "ready" | "error";
export type OrderDetailsEditState = "forbidden" | "unavailable" | "order_only" | "synchronized" | "locked";

export function orderDetailsEditState(
  role: Role,
  workflowStatus: CustomerOrderData["workflowStatus"],
  lookupState: OrderDetailsRequestLookupState,
  requestStatus?: ExecutionRequestData["status"],
): OrderDetailsEditState {
  if (role !== "admin" && role !== "manager") return "forbidden";
  if (workflowStatus === "closed" || workflowStatus === "cancelled") return "locked";
  if (lookupState !== "ready") return "unavailable";
  if (requestStatus === undefined) return "order_only";
  return requestStatus === "new" ? "synchronized" : "locked";
}

export interface AssignableUserData { id: string; displayName: string; role: Role; isActive?: boolean }
export interface OperationalDefaultsData { legalEntityId?:string;procurementWarehouseId?:string;pharmacyWarehouseId?:string;supplierId?:string;retailCounterpartyId?:string;retailCounterpartyCandidateStatus?:"missing"|"unique"|"ambiguous";retailCounterpartyCandidateId?:string;retailCounterpartyCandidateName?:string;paymentMethod:"cash"|"bank"|"card"|"qr"|"mixed";version:number;updatedAt?:string }

export interface NotificationData {
  id:string;
  version:number;
  title:string;
  message:string;
  category:string;
  module:Section;
  documentId?:string;
  deepLink?:string;
  createdAt:string;
  readAt?:string;
  actionStatus:"informational"|"pending"|"resolved";
}
export interface NotificationPageData { items:NotificationData[];total:number;unread:number;offset:number;limit:number }
export interface NotificationCountsData {
  totalUnread:number;
  totalActionable:number;
  totalAttention:number;
  byModule:Partial<Record<Section,number>>;
  byUnreadModule?:Partial<Record<Section,number>>;
  byUnreadView?:Record<string,number>;
  totalUnreadDocuments?:number;
  workOverview?:{total:number;byModule:Partial<Record<Section,number>>;byView:Record<string,number>};
}

export const roleLabels: Record<Role, string> = {
  admin: "Администратор",
  manager: "Менеджер",
  operator: "Управляющий",
  pharmacy: "Аптека",
};

export const navigation: NavItem[] = [
  { id: "dashboard", label: "Показатели", icon: "chart", roles: ["admin", "operator"] },
  { id: "audit", label: "Аудит", icon: "check", roles: ["admin"] },
  { id: "clients", label: "Звонки и клиенты", icon: "users", roles: ["admin", "manager"] },
  { id: "catalog", label: "Товары", icon: "package", roles: ["admin", "manager", "pharmacy"] },
  { id: "purchases", label: "Закупки", icon: "basket", roles: ["admin", "operator"] },
  { id: "inventory", label: "Склад", icon: "warehouse", roles: ["admin", "operator", "pharmacy"] },
  { id: "finance", label: "Деньги", icon: "wallet", roles: ["admin"] },
  { id: "sales", label: "Отчёты", icon: "bag", roles: ["admin", "manager", "operator", "pharmacy"] },
  { id: "tasks", label: "Заявки", icon: "check", roles: ["admin", "manager", "operator"] },
  { id: "retail", label: "Касса и смена", icon: "store", roles: [] },
  { id: "pharmacy_money", label: "Деньги", icon: "wallet", roles: ["pharmacy"] },
  { id: "operator_cash", label: "Касса управляющего", icon: "wallet", roles: ["admin", "operator"] },
  { id: "settings", label: "Настройки", icon: "settings", roles: ["admin"] },
];

export interface LegacyOpsRetailSaleItemData {
  productId: string;
  productName: string;
  quantity: string;
}

export interface LegacyOpsRetailSaleData {
  id: string;
  moyskladId: string;
  number: string;
  status: string;
  paymentMethod: "cash" | "card" | "mixed" | "unknown";
  totalMinor: number;
  cashMinor: number;
  cardMinor: number;
  retailStoreName: string;
  sourceName: string;
  customerOrderId?: string;
  customerOrderNumber?: string;
  occurredAt: string;
  createdAt: string;
  updatedAt: string;
  items: LegacyOpsRetailSaleItemData[];
  sourceSystem: "xc_ops";
  readOnly: true;
}

export interface LegacyOpsRetailSalePageData {
  items: LegacyOpsRetailSaleData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyOpsRetailReturnData {
  id: string;
  orderId?: string;
  status: string;
  productId: string;
  productName: string;
  quantity: string;
  reason?: string;
  pharmacyId?: string;
  createdAt: string;
  updatedAt: string;
  sourceSystem: "xc_ops";
  readOnly: true;
}

export interface LegacyOpsRetailReturnPageData {
  items: LegacyOpsRetailReturnData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyCrmCallOrderData {
  id: string;
  number: string;
  sourceStatus: string;
  crmResult: string;
  amountMinor: number;
  moment?: string;
  sourceSystem: "xc_crm" | "pharmhub";
}

export interface LegacyCrmCallData {
  id: string;
  source: string;
  direction: "incoming" | "outgoing" | "unknown";
  status: "answered" | "missed" | "unknown";
  phone: string;
  startedAt: string;
  endedAt?: string;
  durationSeconds: number;
  contactId?: string;
  contactName?: string;
  contactPhones: string[];
  contactCrmLevel?: ClientData["crmLevel"];
  contactManualControlStatus?: ClientData["manualControlStatus"];
  contactClientStatus?: ClientData["crmLevel"]|"risk";
  lineName?: string;
  linePhone?: string;
  phoneOperator?: string;
  responsibleName?: string;
  result?: string;
  clientLinkEditable: boolean;
  linkStateToken?: string;
  linkedOrders: LegacyCrmCallOrderData[];
  sourceSystem: "xc_crm" | "pharmhub";
  readOnly: boolean;
}

export interface CallClientCreationPreviewData {
  callId: string;
  phone: string;
  callStateToken: string;
  canCreate: boolean;
  reason: "available" | "already_linked" | "active_phone_match" | "archived_phone_match";
  existingClientId?: string;
  existingClientName?: string;
}

export interface CallClientCreateResultData {
  client: ClientData;
  call: LegacyCrmCallData;
}

export interface LegacyCrmCallPageData {
  items: LegacyCrmCallData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyCrmClientData {
  id: string;
  displayName: string;
  phones: string[];
  note?: string;
  source: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  totalCalls: number;
  incomingCalls: number;
  outgoingCalls: number;
  lastCallAt?: string;
  ordersCount: number;
  successfulOrders: number;
  failedOrders: number;
  ordersAmountMinor: number;
  lastOrderAt?: string;
  sharedPhoneCount: number;
  linkState: "verified" | "needs_review";
  sourceSystem: "xc_crm";
  readOnly: true;
}

export interface LegacyCrmClientPageData {
  items: LegacyCrmClientData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyCrmOrderItemData {
  id: string;
  productName: string;
  code?: string;
  article?: string;
  quantity: string;
  priceMinor: number;
  sumMinor: number;
}

export interface LegacyCrmOrderData {
  id: string;
  number: string;
  sourceStatus: string;
  crmResult: string;
  amountMinor: number;
  paidMinor: number;
  shippedMinor: number;
  moment?: string;
  createdAt?: string;
  updatedAt?: string;
  agentName?: string;
  ownerName?: string;
  description?: string;
  linkState: "linked" | "ambiguous" | "unlinked" | "ignored";
  linkedContactIds: string[];
  linkSources: string[];
  ignoredReason?: string;
  items: LegacyCrmOrderItemData[];
  sourceSystem: "xc_crm";
  readOnly: true;
}

export interface LegacyCrmOrderPageData {
  items: LegacyCrmOrderData[];
  total: number;
  offset: number;
  limit: number;
}

export interface LegacyCrmStatisticsData {
  exportedAt: string;
  sourceRevision: string;
  contacts: number;
  contactsWithoutPhone: number;
  phoneAssignments: number;
  uniquePhones: number;
  sharedPhoneValues: number;
  contactsInPhoneReview: number;
  calls: number;
  incomingCalls: number;
  outgoingCalls: number;
  unknownDirectionCalls: number;
  answeredCalls: number;
  missedCalls: number;
  unknownStatusCalls: number;
  matchedCalls: number;
  unmatchedCalls: number;
  orders: number;
  orderItems: number;
  linkedOrders: number;
  ambiguousOrders: number;
  unlinkedOrders: number;
  ignoredOrders: number;
  successfulOrders: number;
  failedOrders: number;
  activeOrders: number;
  retailSales: number;
  retailSaleItems: number;
  sourceSystem: "xc_crm";
  readOnly: true;
}
